import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { DefaultWeightUnits } from '@strength/db-schema';
import {
  ALL_ACCESS_PROFILE_ROLE,
  zOneRepMaxFormula,
  zProfileRoleType,
} from '@strength/domain';
import { useNavigate, useParams } from '@tanstack/react-router';
import { MdExpandMore } from '@teamworksdev/icons';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { trpc } from '../../../../trpc/react';
import { Form, FormField, FormItem } from '../../../components/form/form';
import { Page } from '../../../components/layout/page';
import { PageHeader } from '../../../components/layout/page-header';
import { ConfirmationModal } from '../../../components/modals/confirmation-modal';
import { InfoTooltip } from '../../../components/tooltip/info-tooltip';
import { useToast } from '../../../hooks/useToast';
import { ErrorPage } from '../../error';
import { ProfileRoleSheet } from '../components/ProfileRoleSheet/ProfileRoleSheet';
import {
  PROFILE_ROLE_LABELS,
  ProfileRoleAssignment,
} from '../components/ProfileRoleSheet/types';
import { getProfileRoleLabels } from '../components/utils';

const zProfileRoleAssignment = z.object({
  id: z.string(),
  profileId: z.string(),
  role: zProfileRoleType,
});

const zOrgSettingsForm = z.object({
  oneRepMaxFormula: zOneRepMaxFormula,
  defaultCycleLabel: z.string().min(1, 'Cycle label is required'),
  defaultSessionLabel: z.string().min(1, 'Session label is required'),
  defaultWeightUnit: z.enum(DefaultWeightUnits),
  enableTeamLibrary: z.boolean(),
  showBaseLibrary: z.boolean(),
  requireExerciseMetadataFields: z.boolean(),
  defaultCoachRole: zProfileRoleType,
  athleteUserTypeTakesPrecedence: z.boolean(),
  profileRoles: z.array(zProfileRoleAssignment),
});

type OrgSettingsForm = z.infer<typeof zOrgSettingsForm>;

export function OrgSettingsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const theme = useTheme();
  const navigate = useNavigate();
  const { orgId } = useParams({
    from: '/adminAuth/admin/orgs/$orgId/settings',
  });
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [pendingOrgId, setPendingOrgId] = useState<string>();
  const [teamLibraryModalOpen, setTeamLibraryModalOpen] = useState(false);

  const orgsQuery = trpc.admin.orgs.useQuery(undefined, {
    staleTime: 10_000,
  });
  const org = orgsQuery.data?.find((o) => o.id === orgId);

  const settingsQuery = trpc.admin.getOrgSettings.useQuery({ orgId });

  const teamLibraryStatusQuery =
    trpc.admin.getTeamLibraryStatus.useQuery({ orgId });
  const hasTeamExercises =
    teamLibraryStatusQuery.data?.hasTeamExercises ?? false;

  // The checkbox is read-only once team library is enabled (can't be turned
  // off without an engineering request) or once team-scoped exercises exist.
  const teamLibraryLocked =
    (settingsQuery.data?.enableTeamLibrary ?? false) || hasTeamExercises;

  const orgFeatureFlagsQuery = trpc.admin.getOrgFeatureFlags.useQuery({
    orgId,
  });

  const bowlingBlockTypeFlag =
    orgFeatureFlagsQuery.data?.bowlingBlockTypeFlag ?? false;

  const coachProfilesQuery = trpc.admin.getCoachProfiles.useQuery({ orgId });

  const profileRolesQuery = trpc.admin.getProfileRoles.useQuery({ orgId });

  const updateMutation = trpc.admin.updateOrgSettings.useMutation();

  const createProfileRoleMutation = trpc.admin.createProfileRole.useMutation();
  const updateProfileRoleMutation = trpc.admin.updateProfileRole.useMutation();
  const deleteProfileRoleMutation = trpc.admin.deleteProfileRole.useMutation();

  const initialProfileRoles: ProfileRoleAssignment[] =
    profileRolesQuery.data?.map((pr) => ({
      id: pr.id,
      profileId: pr.profileId,
      role: pr.role,
    })) ?? [];

  const form = useForm<OrgSettingsForm>({
    mode: 'onChange',
    resolver: zodResolver(zOrgSettingsForm),
    defaultValues: {
      oneRepMaxFormula: 'epley',
      defaultCycleLabel: '',
      defaultSessionLabel: '',
      defaultWeightUnit: 'lbs',
      enableTeamLibrary: false,
      showBaseLibrary: false,
      requireExerciseMetadataFields: false,
      defaultCoachRole: ALL_ACCESS_PROFILE_ROLE,
      athleteUserTypeTakesPrecedence: false,
      profileRoles: [],
    },
    values:
      settingsQuery.data && profileRolesQuery.data
        ? {
            oneRepMaxFormula: settingsQuery.data.oneRepMaxFormula,
            defaultCycleLabel: settingsQuery.data.defaultCycleLabel,
            defaultSessionLabel: settingsQuery.data.defaultSessionLabel,
            defaultWeightUnit: settingsQuery.data.defaultWeightUnit,
            enableTeamLibrary: settingsQuery.data.enableTeamLibrary,
            showBaseLibrary: settingsQuery.data.showBaseLibrary,
            requireExerciseMetadataFields:
              settingsQuery.data.requireExerciseMetadataFields,
            defaultCoachRole: settingsQuery.data.defaultCoachRole,
            athleteUserTypeTakesPrecedence:
              settingsQuery.data.athleteUserTypeTakesPrecedence,
            profileRoles: initialProfileRoles,
          }
        : undefined,
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    watch,
    setValue,
  } = form;

  const enableTeamLibrary = watch('enableTeamLibrary');
  const profileRoles = watch('profileRoles');

  const goBack = useCallback(() => {
    navigate({ to: '/admin/orgs' });
  }, [navigate]);

  const goToOrg = useCallback(
    (nextOrgId: string) => {
      navigate({
        to: '/admin/orgs/$orgId/settings',
        params: { orgId: nextOrgId },
      });
    },
    [navigate],
  );

  const onOrgSelect = useCallback(
    (nextOrg: { id: string } | null) => {
      if (!nextOrg || nextOrg.id === orgId) {
        return;
      }
      if (isDirty) {
        setPendingOrgId(nextOrg.id);
        setCancelModalOpen(true);
      } else {
        goToOrg(nextOrg.id);
      }
    },
    [goToOrg, isDirty, orgId],
  );

  const cancelHandler = useCallback(() => {
    if (isDirty) {
      setCancelModalOpen(true);
    } else {
      goBack();
    }
  }, [isDirty, goBack]);

  const updateProfileRoles = async (
    formProfileRoles: ProfileRoleAssignment[],
  ) => {
    const originalIds = new Set(initialProfileRoles.map((pr) => pr.id));
    const currentIds = new Set(formProfileRoles.map((pr) => pr.id));

    // Roles to create (in form but not in original)
    const rolesToCreate = formProfileRoles.filter(
      (pr) => !originalIds.has(pr.id),
    );

    // Roles to delete (in original but not in form)
    const rolesToDelete = initialProfileRoles.filter(
      (pr) => !currentIds.has(pr.id),
    );

    // Roles to update (same id, different role or profileId)
    const rolesToUpdate = formProfileRoles.filter((pr) => {
      const original = initialProfileRoles.find((o) => o.id === pr.id);
      return (
        original &&
        (original.role !== pr.role || original.profileId !== pr.profileId)
      );
    });

    // Execute mutations
    await Promise.all([
      ...rolesToCreate.map((role) =>
        createProfileRoleMutation.mutateAsync({
          orgId,
          profileId: role.profileId,
          role: role.role,
        }),
      ),
      ...rolesToDelete.map((role) =>
        deleteProfileRoleMutation.mutateAsync({
          orgId,
          profileId: role.profileId,
          profileRoleId: role.id,
        }),
      ),
      ...rolesToUpdate.map((role) =>
        updateProfileRoleMutation.mutateAsync({
          orgId,
          profileId: role.profileId,
          profileRoleId: role.id,
          role: role.role,
        }),
      ),
    ]);

    // Refetch to sync state
    profileRolesQuery.refetch();
  };

  const onSubmit = async (data: OrgSettingsForm) => {
    try {
      // Save org settings (exclude profileRoles from settings mutation)
      const { profileRoles: formProfileRoles, ...settingsData } = data;
      await updateMutation.mutateAsync({
        orgId,
        ...settingsData,
      });

      await updateProfileRoles(formProfileRoles);

      toast({
        title: 'Settings saved',
        severity: 'success',
      });
      goBack();
    } catch {
      toast({
        title: 'Error saving settings',
        severity: 'error',
      });
    }
  };

  return (
    settingsQuery.error ? (<ErrorPage/>) : (<Page>
      <PageHeader
        title={t('settings')}
        rightSlot={
          <div className="flex items-center gap-2">
            <TextField
              select
              value={org?.id ?? ''}
              onChange={(e) => onOrgSelect({ id: e.target.value })}
              className="w-64 mt-0"
            >
              {(orgsQuery.data ?? []).map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              onClick={cancelHandler}
              sx={{ height: 40 }}
            >
              {t('back')}
            </Button>
          </div>
        }
      />

      <div className="flex flex-col grow px-6 py-6 gap-6 overflow-y-auto">
        {settingsQuery.isLoading || profileRolesQuery.isLoading ? (
          <div>
            <CircularProgress />
          </div>
        ) : (
          <Form {...form}>
            <form
              id="org-settings-form"
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col grow gap-4"
            >
              <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={<MdExpandMore />}>
                  <Typography
                    sx={{ color: theme.palette.mediumEmphasisTextLight }}
                    className="font-semibold"
                  >
                    TUM Settings
                  </Typography>
                </AccordionSummary>

                <AccordionDetails className="flex flex-col gap-4">
                  <FormField
                    name="athleteUserTypeTakesPrecedence"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={(e) =>
                                field.onChange(e.target.checked)
                              }
                            />
                          }
                          label={
                            <span className="flex items-center gap-1">
                              Treat profiles with the Athlete user type as
                              athletes only
                              <InfoTooltip description="Turn on when a second user type is a roster tag rather than a coaching assignment" />
                            </span>
                          }
                        />
                      </FormItem>
                    )}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={<MdExpandMore />}>
                  <Typography
                    sx={{ color: theme.palette.mediumEmphasisTextLight }}
                    className="font-semibold"
                  >
                    Org defaults
                  </Typography>
                </AccordionSummary>

                <AccordionDetails className="flex flex-col gap-4">
                  <FormField
                    name="oneRepMaxFormula"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormItem>
                        <TextField
                          {...field}
                          select
                          label="1RM Formula"
                          error={Boolean(error)}
                          helperText={error?.message}
                          fullWidth
                          disabled
                        >
                          {zOneRepMaxFormula.options.map((formula) => (
                            <MenuItem key={formula} value={formula}>
                              {formula}
                            </MenuItem>
                          ))}
                        </TextField>
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="defaultWeightUnit"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormItem>
                        <TextField
                          {...field}
                          select
                          label="Default Weight Unit"
                          error={Boolean(error)}
                          helperText={error?.message}
                          fullWidth
                          disabled
                        >
                          {DefaultWeightUnits.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </TextField>
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="defaultCycleLabel"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormItem>
                        <TextField
                          {...field}
                          label="Default Cycle Label"
                          error={Boolean(error)}
                          helperText={error?.message}
                          fullWidth
                          disabled
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="defaultSessionLabel"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormItem>
                        <TextField
                          {...field}
                          label="Default Session Label"
                          error={Boolean(error)}
                          helperText={error?.message}
                          fullWidth
                          disabled
                        />
                      </FormItem>
                    )}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={<MdExpandMore />}>
                  <Typography
                    sx={{ color: theme.palette.mediumEmphasisTextLight }}
                    className="font-semibold"
                  >
                    Exercise Library
                  </Typography>
                </AccordionSummary>

                <AccordionDetails className="flex flex-col gap-4">
                  <FormField
                    name="enableTeamLibrary"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={field.value}
                              disabled={
                                !teamLibraryStatusQuery.data ||
                                teamLibraryLocked
                              }
                              onChange={(e) => {
                                // Confirm before enabling: once saved as
                                // enabled, this setting can't be turned off
                                // without an engineering request.
                                if (!field.value && e.target.checked) {
                                  setTeamLibraryModalOpen(true);
                                  return;
                                }
                                field.onChange(e.target.checked);
                              }}
                            />
                          }
                          label={
                            <span className="flex items-center gap-1">
                              Enable Team Library
                              {teamLibraryLocked && (
                                <InfoTooltip description="Team library is enabled and cannot be turned off without an engineering request" />
                              )}
                            </span>
                          }
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="showBaseLibrary"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={(e) =>
                                field.onChange(e.target.checked)
                              }
                              disabled={!enableTeamLibrary}
                            />
                          }
                          label="Show Base Library"
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="requireExerciseMetadataFields"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={(e) =>
                                field.onChange(e.target.checked)
                              }
                            />
                          }
                          label="Require Exercise Metadata Fields (Tags, Body Parts, Equipment)"
                        />
                      </FormItem>
                    )}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={<MdExpandMore />}>
                  <Typography
                    sx={{ color: theme.palette.mediumEmphasisTextLight }}
                    className="font-semibold"
                  >
                    Profile Roles
                  </Typography>
                </AccordionSummary>

                <AccordionDetails className="flex flex-col gap-4">
                <FormField
                  name="defaultCoachRole"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <TextField
                        {...field}
                        select
                        label="Default Coach Role"
                        error={Boolean(error)}
                        helperText={
                          error?.message ||
                          'Default role for coaches without an explicit role assignment'
                        }
                        fullWidth
                      >
                        {getProfileRoleLabels(bowlingBlockTypeFlag).map(
                          (role) => (
                            <MenuItem key={role} value={role}>
                              {PROFILE_ROLE_LABELS[role]}
                            </MenuItem>
                          ),
                        )}
                      </TextField>
                    </FormItem>
                  )}
                />

                <div>
                  <InputLabel
                    sx={{ color: theme.palette.mediumEmphasisTextLight }}
                    className="font-semibold"
                  >
                    Coach Role Assignments
                  </InputLabel>

                  {profileRolesQuery.isLoading || coachProfilesQuery.isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <ProfileRoleSheet
                      bowlingBlockTypeFlag={bowlingBlockTypeFlag}
                      profileRoles={profileRoles}
                      coaches={coachProfilesQuery.data ?? []}
                      onChange={(roles) =>
                        setValue('profileRoles', roles, { shouldDirty: true })
                      }
                    />
                  )}
                </div>
                </AccordionDetails>
              </Accordion>

              <div className="flex flex-row gap-2 justify-end">
                <Button variant="outlined" onClick={cancelHandler}>
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  form="org-settings-form"
                  disabled={!isDirty || updateMutation.isLoading}
                >
                  {t('save')}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </div>

      <ConfirmationModal
        id="enable-team-library"
        open={teamLibraryModalOpen}
        title="Enable Team Library"
        message="Once enabled, the team library cannot be turned off without an engineering request. Are you sure you want to enable the team library?"
        onConfirm={() => {
          setTeamLibraryModalOpen(false);
          setValue('enableTeamLibrary', true, { shouldDirty: true });
        }}
        onCancel={() => setTeamLibraryModalOpen(false)}
      />

      <ConfirmationModal
        id="cancel-org-settings"
        open={cancelModalOpen}
        title="Discard Changes"
        message="Are you sure you want to discard your changes?"
        onConfirm={() => {
          setCancelModalOpen(false);
          if (pendingOrgId) {
            setPendingOrgId(undefined);
            goToOrg(pendingOrgId);
          } else {
            goBack();
          }
        }}
        onCancel={() => setCancelModalOpen(false)}
      />
    </Page>)
  );
}
